import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Ship, Clock, CheckCircle2, Package, MapPin, X, ArrowLeft, Calendar, ChevronRight, Anchor } from 'lucide-react';

// Timeline Modal with consistent glassmorphism
const TimelineModal = ({ shipment, onClose }) => {
    if (!shipment) return null;
    const s = shipment;

    const timelineEvents = [
        { date: '2026-03-04', event: 'Laden Return Time', location: `${s.route.from}, ${s.route.fromCode.slice(0, 2)}`, status: 'completed' },
        { date: '2026-03-08', event: 'Gate In', location: 'Terminal, Berth 64', status: 'completed' },
        { date: '2026-03-10', event: 'Vessel departure from First POL', location: s.route.from, status: 'completed' },
        { date: '2026-03-15', event: 'Transshipment arrival', location: 'Singapore', status: s.progress > 30 ? 'completed' : 'pending' },
        { date: '2026-03-18', event: 'Transshipment departure', location: 'Singapore', status: s.progress > 40 ? 'completed' : 'pending' },
        { date: '2026-04-05', event: 'In transit — ocean crossing', location: 'At Sea', status: s.progress > 50 ? 'completed' : s.progress > 40 ? 'in_progress' : 'pending' },
        { date: s.eta?.split(',')[0]?.trim() || '2026-04-20', event: 'Arrival at destination port', location: s.route.to, status: s.progress >= 100 ? 'completed' : 'pending' },
        { date: '', event: 'Customs clearance', location: s.route.to, status: s.phase === 'delivered' ? 'completed' : 'pending' },
        { date: '', event: 'Container available for pickup', location: s.route.to, status: s.phase === 'delivered' ? 'completed' : 'pending' },
    ];

    const statusBadge = {
        completed: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30',
        in_progress: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/30',
        pending: 'bg-slate-500/10 text-slate-500 dark:text-slate-400 border border-slate-500/20',
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" onClick={onClose}>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 bg-black/30 backdrop-blur-md"
            />
            <motion.div
                initial={{ scale: 0.96, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="relative z-10 w-full max-w-2xl mx-4 max-h-[85vh] overflow-hidden flex flex-col rounded-2xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border border-white/40 dark:border-white/15 shadow-2xl"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-6 py-4 flex items-center justify-between flex-shrink-0 bg-gradient-to-r from-rose-500/80 to-pink-500/80 backdrop-blur-md border-b border-white/30 dark:border-white/15">
                    <div className="flex items-center gap-3">
                        <button onClick={onClose} className="p-1.5 hover:bg-white/25 rounded-lg transition-all duration-200"><ArrowLeft className="w-4 h-4 text-white" /></button>
                        <Ship className="w-5 h-5 text-white" />
                        <div>
                            <h2 className="text-white font-bold text-base">Shipment Timeline</h2>
                            <p className="text-white/85 text-xs">{s.po} — {s.containers[0]?.id}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 hover:bg-white/25 rounded-lg transition-all duration-200"><X className="w-5 h-5 text-white" /></button>
                </div>

                <div className="overflow-y-auto flex-1 p-6 space-y-5">
                    {/* Info Cards */}
                    <div className="grid grid-cols-4 gap-3">
                        {[
                            ['Container', s.containers[0]?.id || 'N/A'],
                            ['Carrier', s.vessel.split(' ')[0] || 'N/A'],
                            ['Vessel / Voyage', `${s.vessel} / ${s.voyage}`],
                            ['Supplier', s.supplier.split(' ').slice(0, 2).join(' ')]
                        ].map(([label, val]) => (
                            <div key={label} className="bg-white/40 dark:bg-white/5 backdrop-blur-sm border border-white/50 dark:border-white/10 rounded-xl p-3">
                                <p className="text-[10px] text-slate-600 dark:text-slate-400 mb-0.5">{label}</p>
                                <p className="text-sm font-bold text-slate-800 dark:text-white leading-tight">{val}</p>
                            </div>
                        ))}
                    </div>

                    {/* Route Card */}
                    <div className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 dark:from-blue-500/5 dark:to-indigo-500/5 backdrop-blur-sm border border-blue-500/30 dark:border-blue-400/20 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <p className="text-[10px] text-slate-600 dark:text-slate-400">Origin</p>
                                <p className="text-lg font-bold text-slate-800 dark:text-white">{s.route.fromCode}</p>
                                <p className="text-xs text-slate-600 dark:text-slate-400">{s.route.from}</p>
                            </div>
                            <div className="flex-1 mx-4 flex items-center">
                                <div className="flex-1 h-px bg-gradient-to-r from-blue-400/50 to-blue-400/20 dark:from-blue-400/30" />
                                <Ship className="w-5 h-5 text-blue-500/60 mx-2" />
                                <div className="flex-1 h-px bg-gradient-to-l from-blue-400/50 to-blue-400/20 dark:from-blue-400/30" />
                            </div>
                            <div className="text-right">
                                <p className="text-[10px] text-slate-600 dark:text-slate-400">Destination</p>
                                <p className="text-lg font-bold text-slate-800 dark:text-white">{s.route.toCode}</p>
                                <p className="text-xs text-slate-600 dark:text-slate-400">{s.route.to}</p>
                            </div>
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t border-white/30 dark:border-white/10">
                            <div>
                                <p className="text-[9px] text-slate-500">ETD</p>
                                <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">2026-03-10</p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm border ${s.phase === 'delayed' ? 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30' :
                                s.phase === 'delivered' ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30' :
                                    'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30'
                                }`}>
                                {s.phase === 'delayed' ? 'Delayed' : s.phase === 'delivered' ? 'Delivered' : 'In Transit'}
                            </span>
                            <div className="text-right">
                                <p className="text-[9px] text-slate-500">ETA</p>
                                <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">{s.eta}</p>
                            </div>
                        </div>
                    </div>

                    {/* Progress */}
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Transit Progress</span>
                            <span className="text-xs font-bold text-slate-700 dark:text-white">{s.progress}%</span>
                        </div>
                        <div className="h-2 bg-slate-200/60 dark:bg-slate-700/40 rounded-full overflow-hidden backdrop-blur-sm">
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${s.progress}%` }}
                                transition={{ duration: 1 }}
                                className={`h-full rounded-full bg-gradient-to-r ${s.phase === 'delayed' ? 'from-red-500 to-amber-500' : 'from-blue-500 to-cyan-400'
                                    } opacity-90`}
                            />
                        </div>
                    </div>

                    {/* Tracking Timeline */}
                    <h3 className="text-base font-bold text-slate-800 dark:text-white">Tracking Timeline</h3>
                    <div className="space-y-0">
                        {timelineEvents.map((ev, i) => (
                            <div key={i} className="flex gap-3">
                                <div className="flex flex-col items-center">
                                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${ev.status === 'completed' ? 'bg-emerald-500 shadow-lg shadow-emerald-500/30' :
                                        ev.status === 'in_progress' ? 'bg-blue-500 ring-2 ring-blue-500/40' :
                                            'bg-slate-400/40 dark:bg-slate-600/40'
                                        }`} />
                                    {i < timelineEvents.length - 1 && (
                                        <div className={`w-px flex-1 min-h-[42px] ${ev.status === 'completed' ? 'bg-gradient-to-b from-emerald-400/60 to-emerald-400/10' :
                                            'bg-slate-300/40 dark:bg-slate-600/30'
                                            }`} />
                                    )}
                                </div>
                                <div className="pb-5 flex-1">
                                    <div className="bg-white/40 dark:bg-white/5 backdrop-blur-sm border border-white/50 dark:border-white/10 rounded-xl p-3">
                                        <div className="flex items-center justify-between mb-0.5">
                                            <p className="text-[10px] text-slate-500 dark:text-slate-400">{ev.date || 'Pending'}</p>
                                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold backdrop-blur-sm ${statusBadge[ev.status]}`}>
                                                {ev.status === 'completed' ? 'Completed' : ev.status === 'in_progress' ? 'In Progress' : 'Pending'}
                                            </span>
                                        </div>
                                        <p className="text-sm font-semibold text-slate-800 dark:text-white">{ev.event}</p>
                                        <p className="text-[11px] text-slate-600 dark:text-slate-400 flex items-center gap-1 mt-1">
                                            <MapPin className="w-3 h-3" />{ev.location}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

const ContainerTrackingDashboard = () => {
    const [selectedPO, setSelectedPO] = useState(null);
    const [fromDate, setFromDate] = useState('2026-04-01');
    const [toDate, setToDate] = useState('2026-05-30');

    const poShipments = [
        {
            po: 'PO-4521', supplier: 'Shanghai Electronics Co.', poValue: '$48,500',
            containers: [{ id: 'MSCU7234561', type: '40HC', loaded: true }, { id: 'MSCU7234562', type: '40HC', loaded: true }],
            vessel: 'MSC OSCAR', voyage: '246W',
            route: { from: 'Shanghai', fromCode: 'CNSHA', to: 'Los Angeles', toCode: 'USLAX' },
            loadStatus: 'Fully Loaded', phase: 'on_water', eta: 'May 22, 2026', progress: 72,
            cycle: [
                { step: 'PO Placed', done: true, date: 'Apr 10' }, { step: 'Supplier Confirmed', done: true, date: 'Apr 12' },
                { step: 'Cargo Ready', done: true, date: 'Apr 28' },
                { step: 'Fully Loaded', done: true, date: 'May 02' },
                { step: 'On Water', done: true, current: true, date: 'May 03' }, { step: 'Pre-Alert', done: false },
                { step: 'Arrived', done: false },
                { step: 'Customs Cleared', done: false }, { step: 'Delivered', done: false },
            ],
            lastUpdate: 'Passed Midway Point — Pacific Ocean', daysLeft: 8,
        },
        {
            po: 'PO-4519', supplier: 'Thai Chin Fu Enterprise', poValue: '$32,200',
            containers: [{ id: 'COSCU9876543', type: '40HC', loaded: true }],
            vessel: 'COSCO UNIVERSE', voyage: '089E',
            route: { from: 'Bangkok', fromCode: 'THBKK', to: 'Long Beach', toCode: 'USLGB' },
            loadStatus: 'Fully Loaded', phase: 'arriving', eta: 'May 18, 2026', progress: 88,
            cycle: [
                { step: 'PO Placed', done: true, date: 'Mar 28' }, { step: 'Supplier Confirmed', done: true, date: 'Mar 30' },
                { step: 'Cargo Ready', done: true, date: 'Apr 15' },
                { step: 'Fully Loaded', done: true, date: 'Apr 18' },
                { step: 'On Water', done: true, date: 'Apr 19' }, { step: 'Pre-Alert', done: true, date: 'May 14' },
                { step: 'Arrived', done: true, current: true, date: 'May 16' },
                { step: 'Customs Cleared', done: false }, { step: 'Delivered', done: false },
            ],
            lastUpdate: 'Vessel approaching Long Beach — 2 days out', daysLeft: 2,
        },
        {
            po: 'PO-4517', supplier: 'Rotterdam Chemical BV', poValue: '$125,000',
            containers: [{ id: 'HLCU4567890', type: '20GP', loaded: true }, { id: 'Pending', type: '20GP', loaded: false }, { id: 'Pending', type: '20GP', loaded: false }],
            vessel: 'Pending assignment', voyage: '-',
            route: { from: 'Rotterdam', fromCode: 'NLRTM', to: 'Houston', toCode: 'USHOU' },
            loadStatus: '1 of 3 Loaded', phase: 'loading', eta: 'Jun 05, 2026', progress: 15,
            cycle: [
                { step: 'PO Placed', done: true, date: 'Apr 20' }, { step: 'Supplier Confirmed', done: true, date: 'Apr 22' },
                { step: 'Cargo Ready', done: true, current: true, date: 'May 10' },
                { step: 'Fully Loaded', done: false },
                { step: 'On Water', done: false }, { step: 'Pre-Alert', done: false },
                { step: 'Arrived', done: false },
                { step: 'Customs Cleared', done: false }, { step: 'Delivered', done: false },
            ],
            lastUpdate: '1 of 3 containers loaded — awaiting remaining cargo from supplier', daysLeft: 22,
        },
        {
            po: 'PO-4515', supplier: 'Mumbai Textile Exports', poValue: '$67,800',
            containers: [{ id: 'TEMU8901234', type: '40HC', loaded: true }, { id: 'TEMU8901235', type: '40HC', loaded: true }, { id: 'TEMU8901236', type: '40HC', loaded: true }],
            vessel: 'EVER GIVEN', voyage: '052W',
            route: { from: 'Mumbai', fromCode: 'INBOM', to: 'Felixstowe', toCode: 'GBFXT' },
            loadStatus: 'Fully Loaded', phase: 'delayed', eta: 'May 30, 2026 (was May 25)', progress: 55,
            cycle: [
                { step: 'PO Placed', done: true, date: 'Apr 05' }, { step: 'Supplier Confirmed', done: true, date: 'Apr 07' },
                { step: 'Cargo Ready', done: true, date: 'Apr 22' },
                { step: 'Fully Loaded', done: true, date: 'Apr 25' },
                { step: 'On Water', done: true, current: true, date: 'Apr 26' }, { step: 'Pre-Alert', done: false },
                { step: 'Arrived', done: false },
                { step: 'Customs Cleared', done: false }, { step: 'Delivered', done: false },
            ],
            lastUpdate: 'Delayed 5 days — port congestion at Suez approach', daysLeft: 16,
        },
        {
            po: 'PO-4513', supplier: 'Korea Auto Parts Ltd', poValue: '$89,300',
            containers: [{ id: 'OOLU3216549', type: '40GP', loaded: true }, { id: 'OOLU3216550', type: '40GP', loaded: true }],
            vessel: 'HAPAG EXPRESS', voyage: '312S',
            route: { from: 'Busan', fromCode: 'KRPUS', to: 'New York', toCode: 'USNYC' },
            loadStatus: 'Fully Loaded', phase: 'delivered', eta: 'May 10, 2026', progress: 100,
            cycle: [
                { step: 'PO Placed', done: true, date: 'Mar 15' }, { step: 'Supplier Confirmed', done: true, date: 'Mar 17' },
                { step: 'Cargo Ready', done: true, date: 'Apr 02' },
                { step: 'Fully Loaded', done: true, date: 'Apr 05' },
                { step: 'On Water', done: true, date: 'Apr 06' }, { step: 'Pre-Alert', done: true, date: 'May 06' },
                { step: 'Arrived', done: true, date: 'May 08' },
                { step: 'Customs Cleared', done: true, date: 'May 09' }, { step: 'Delivered', done: true, current: true, date: 'May 10' },
            ],
            lastUpdate: 'All containers delivered to warehouse', daysLeft: 0,
        },
    ];

    const phaseConfig = {
        on_water: {
            label: 'On Water',
            badge: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/30',
            dot: 'bg-blue-500',
            bar: 'from-blue-500 to-cyan-400',
            noteBg: 'bg-blue-500/5',
            icon: '🚢'
        },
        arriving: {
            label: 'Arriving',
            badge: 'bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border border-cyan-500/30',
            dot: 'bg-cyan-500',
            bar: 'from-cyan-500 to-teal-400',
            noteBg: 'bg-cyan-500/5',
            icon: '📍'
        },
        loading: {
            label: 'Loading',
            badge: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30',
            dot: 'bg-amber-500',
            bar: 'from-amber-500 to-yellow-400',
            noteBg: 'bg-amber-500/5',
            icon: '📦'
        },
        delayed: {
            label: 'Delayed',
            badge: 'bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/30',
            dot: 'bg-red-500',
            bar: 'from-red-500 to-amber-400',
            noteBg: 'bg-red-500/5',
            icon: '⚠️'
        },
        delivered: {
            label: 'Delivered',
            badge: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30',
            dot: 'bg-emerald-500',
            bar: 'from-emerald-500 to-green-400',
            noteBg: 'bg-emerald-500/5',
            icon: '✅'
        },
    };

    const summary = { onWater: 1, arriving: 1, loading: 1, delayed: 1, delivered: 1 };

    return (
        <>
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="rounded-2xl shadow overflow-hidden bg-white/60 dark:bg-slate-900/60 backdrop-blur-xl border border-white/50 dark:border-white/15"
                data-testid="container-tracking-dashboard"
            >
                {/* Header */}
                <div className="px-5 py-4 border-b border-white/50 dark:border-white/15 bg-gradient-to-r from-white/40 to-white/20 dark:from-white/5 dark:to-transparent backdrop-blur-sm">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center">
                                <Anchor className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-slate-800 dark:text-white">POs on the Water</h3>
                                <p className="text-[11px] text-slate-600 dark:text-slate-400">Track every PO — see what's loaded, sailing, or stuck</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 bg-white/10 dark:bg-white/10 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-slate-200 dark:border-white/15">
                                <Calendar className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
                                <input
                                    type="date"
                                    value={fromDate}
                                    onChange={e => setFromDate(e.target.value)}
                                    className="bg-transparent text-xs text-slate-700 dark:text-slate-300 border-none outline-none w-28"
                                />
                                <span className="text-slate-400 text-xs">—</span>
                                <input
                                    type="date"
                                    value={toDate}
                                    onChange={e => setToDate(e.target.value)}
                                    className="bg-transparent text-xs text-slate-700 dark:text-slate-300 border-none outline-none w-28"
                                />
                            </div>
                            <div className=" hidden flex gap-1.5">
                                {Object.entries(summary).filter(([, v]) => v > 0).map(([k, v]) => (
                                    <span key={k} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold backdrop-blur-sm ${phaseConfig[k === 'onWater' ? 'on_water' : k]?.badge}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${phaseConfig[k === 'onWater' ? 'on_water' : k]?.dot} opacity-80`} />
                                        {v}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Shipment List */}
                <div className="divide-slate-200/60 dark:divide-white/10">
                    {poShipments.map((s, i) => {
                        const pc = phaseConfig[s.phase];
                        return (
                            <motion.div
                                key={s.po}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.05 }}
                                className="m-3 p-4 rounded-xl transition-all duration-200 bg-white/60 dark:bg-slate-950/10 backdrop-blur-sm border border-slate-300/70 dark:border-white/10 shadow-sm hover:shadow-md hover:bg-white/80 dark:hover:bg-slate-950/30"
                            >
                                {/* Header Row */}
                                <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                                    <div className="flex items-center justify-center w-10 h-10 rounded-lg 
                                            bg-blue-100/60 text-blue-700 
                                            dark:bg-blue-900/20 dark:text-blue-300 
                                            backdrop-blur-md border border-blue-200/30 dark:border-blue-800/30 
                                            font-bold text-[11px] tabular-nums shrink-0">
                                        {s.po.replace('PO-', '#')}
                                    </div>
                                    <div className="flex-1">

                                        <div className="flex items-center gap-2 mb-1 flex-wrap">

                                            <span className="text-sm font-bold text-slate-800 dark:text-white">{s.po}</span>
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold backdrop-blur-sm ${pc.badge}`}>
                                                {pc.label}
                                            </span>
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold backdrop-blur-sm ${s.loadStatus.includes('Fully') ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-400/50 dark:border-emerald-500/30' :
                                                'bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-400/50 dark:border-amber-500/30'
                                                }`}>
                                                {s.loadStatus}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3 text-[11px] text-slate-600 dark:text-slate-400 flex-wrap">
                                            <span className="font-medium text-slate-700 dark:text-slate-300">{s.supplier}</span>
                                            <span className="font-semibold text-slate-900 dark:text-slate-100">
                                                {s.poValue}
                                            </span>
                                            {s.vessel !== 'Pending assignment' && (
                                                <span className="flex items-center gap-1"><Ship className="w-3 h-3" />{s.vessel}</span>
                                            )}
                                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />ETA: {s.eta}</span>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {s.daysLeft > 0 && (
                                            <span className={`text-xs font-bold px-2.5 py-1 rounded-lg backdrop-blur-sm border ${s.phase === 'delayed' ? 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-400/50 dark:border-red-500/30' :
                                                'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-400/50 dark:border-blue-500/30'
                                                }`}>
                                                {s.daysLeft}d
                                            </span>
                                        )}
                                        <button
                                            onClick={() => setSelectedPO(s)}
                                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-[12px] font-semibold transition-colors shadow-md shadow-rose-500/20"
                                            data-testid={`timeline-btn-${s.po}`}
                                        >
                                            Timeline
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                {/* Containers */}
                                <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                                    {s.containers.map((c, ci) => (
                                        <span key={ci} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-mono backdrop-blur-sm ${c.loaded ? 'bg-white/70 dark:bg-slate-800/40 text-slate-700 dark:text-slate-300 border border-slate-400/50 dark:border-slate-700/40' :
                                            'bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-400/50 dark:border-amber-500/30'
                                            }`}>
                                            {c.loaded ? <CheckCircle2 className="w-2.5 h-2.5 text-emerald-500" /> : <Clock className="w-2.5 h-2.5 text-amber-500" />}
                                            {c.id === 'Pending' ? `${c.type} — Pending` : `${c.id} (${c.type})`}
                                        </span>
                                    ))}
                                </div>

                                {/* Progress Bar with Route */}
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="text-[10px] font-semibold text-slate-700 dark:text-slate-300 bg-white/70 dark:bg-white/10 backdrop-blur-sm px-2 py-0.5 rounded border border-slate-400/50 dark:border-white/15">
                                        {s.route.from.toUpperCase()} [{s.route.fromCode}]
                                    </span>
                                    <div className="flex-1 relative h-2 bg-slate-200/80 dark:bg-slate-700/40 rounded-full overflow-hidden backdrop-blur-sm">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${s.progress}%` }}
                                            transition={{ duration: 1, delay: i * 0.1 }}
                                            className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r ${pc.bar} opacity-90`}
                                        />
                                        {s.progress > 5 && s.progress < 95 && (
                                            <motion.div
                                                initial={{ left: 0 }}
                                                animate={{ left: `${s.progress}%` }}
                                                transition={{ duration: 1, delay: i * 0.1 }}
                                                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
                                            >
                                                <Ship className="w-3 h-3 text-blue-500 dark:text-blue-400 drop-shadow-sm" />
                                            </motion.div>
                                        )}
                                    </div>
                                    <span className="text-[10px] font-semibold text-slate-700 dark:text-slate-300 bg-white/70 dark:bg-white/10 backdrop-blur-sm px-2 py-0.5 rounded border border-slate-400/50 dark:border-white/15">
                                        {s.route.to.toUpperCase()} [{s.route.toCode}]
                                    </span>
                                    <span className="text-[10px] text-slate-500 w-8 text-right">{s.progress}%</span>
                                </div>

                                {/* Cycle Steps */}
                                <div className="flex items-center gap-0 mb-3 overflow-x-auto pb-1">
                                    {s.cycle.map((step, si) => (
                                        <React.Fragment key={si}>
                                            <div className="flex flex-col items-center flex-shrink-0" title={`${step.step}${step.date ? ` — ${step.date}` : ''}`}>
                                                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold backdrop-blur-sm ${step.done ? (
                                                    step.current ?
                                                        'bg-blue-500 text-white shadow-md shadow-blue-500/30 ring-2 ring-blue-500/40 scale-110' :
                                                        'bg-emerald-500 text-white shadow-sm shadow-emerald-500/30'
                                                ) : 'bg-white/70 dark:bg-slate-800/40 text-slate-400 border border-slate-400/50 dark:border-slate-700/40'
                                                    }`}>
                                                    {step.done ? '✓' : ''}
                                                </div>
                                                <span className={`text-[9px] mt-1 text-center leading-tight w-14 ${step.current ? 'text-blue-600 dark:text-blue-400 font-bold' :
                                                    step.done ? 'text-slate-700 dark:text-slate-200 font-medium' :
                                                        'text-slate-500 dark:text-slate-400'
                                                    }`}>{step.step}</span>
                                            </div>
                                            {si < s.cycle.length - 1 && (
                                                <div className={`flex-1 h-px mx-0.5 ${step.done ? 'bg-gradient-to-r from-emerald-400/60 to-emerald-400/10' :
                                                    'bg-slate-300/60 dark:bg-slate-600/30'
                                                    }`} />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>

                                {/* Note Section */}
                                {s.lastUpdate && (
                                    <div className={`inline-flex items-center gap-2 px-3 h-7 rounded-md backdrop-blur-sm border ${pc.noteBg || 'bg-white/50 dark:bg-white/5'} ${s.phase === 'on_water' ? 'border-blue-400/50 dark:border-blue-500/30' :
                                        s.phase === 'arriving' ? 'border-cyan-400/50 dark:border-cyan-500/30' :
                                            s.phase === 'loading' ? 'border-amber-400/50 dark:border-amber-500/30' :
                                                s.phase === 'delayed' ? 'border-red-400/50 dark:border-red-500/30' :
                                                    s.phase === 'delivered' ? 'border-emerald-400/50 dark:border-emerald-500/30' :
                                                        'border-slate-400/50 dark:border-white/15'
                                        }`}>
                                        <span className="text-[13px]">{pc.icon}</span>
                                        <span className={`w-1.5 h-1.5 rounded-full ${pc.dot} opacity-80`} />
                                        <span className="text-[11.5px] font-medium text-slate-700 dark:text-slate-300">{s.lastUpdate}</span>
                                    </div>
                                )}
                            </motion.div>
                        );
                    })}
                </div>
            </motion.div>

            <AnimatePresence>
                {selectedPO && <TimelineModal shipment={selectedPO} onClose={() => setSelectedPO(null)} />}
            </AnimatePresence>
        </>
    );
};

export default ContainerTrackingDashboard;